import {Injectable, signal} from '@angular/core';
import {Location} from '../../domain/models/location.model';
import {Drawer} from '../../domain/models/drawer.model';
import {Worker} from '../../domain/models/worker.model';
import {ServiceRelations} from '@features/service/domain/models/service-relations.model';
import {ServiceStorePort} from '../../domain/ports/service-store.port';

export interface ServiceSelection {
  location: Location | null;
  drawer: Drawer | null;
  worker: Worker | null;
}

export interface ServiceState {
  locations: Location[];
  drawers: Drawer[];
  workers: Worker[];
  selection: ServiceSelection | null;
  serviceRelations: ServiceRelations | null;
  isWorkersOwnedDevice: boolean;
  isLoading: boolean;
}

const INITIAL_SERVICE_STATE: ServiceState = {
  locations: [],
  drawers: [],
  workers: [],
  selection: null,
  serviceRelations: null,
  isWorkersOwnedDevice: false,
  isLoading: false,
};

@Injectable({providedIn: 'root'})
export class ServiceStore implements ServiceStorePort {
  private readonly _state = signal(INITIAL_SERVICE_STATE);
  readonly state = this._state.asReadonly();

  private isInitialState(state: ServiceState): boolean {
    return (
      state.locations.length === 0 &&
      state.drawers.length === 0 &&
      state.workers.length === 0 &&
      state.serviceRelations === null
    );
  }

  checkIfInitialState(): boolean {
    return this.isInitialState(this.state());
  }

  setLocations(locations: Location[]): void {
    this.patch({locations});
  }

  setDrawers(drawers: Drawer[]): void {
    this.patch({drawers});
  }

  setWorkers(workers: Worker[]): void {
    this.patch({workers});
  }

  setServiceRelations(relations: ServiceRelations): void {
    this.patch({serviceRelations: relations});
  }

  setSelection(selection: Partial<ServiceSelection>): void {
    this.patch({selection: {...this.state().selection, ...selection} as ServiceSelection});
  }

  setIsWorkersOwnedDevice(isWorkersOwnedDevice: boolean): void {
    this.patch({isWorkersOwnedDevice});
  }

  setIsLoading(isLoading: boolean): void {
    this.patch({isLoading});
  }

  reset(): void {
    this._state.set(INITIAL_SERVICE_STATE);
  }

  patch(partial: Partial<ServiceState>): void {
    this._state.update((current) => ({...current, ...partial}));
  }
}
